import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import api, { formatApiErrorDetail } from "@/lib/api";
import { ArcButton } from "@/components/ArcButton";
import { LabelBadge } from "@/components/LabelBadge";
import { Plus, Pencil, Trash2, ArrowLeft } from "lucide-react";
import { toast } from "sonner";
import {
  AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent,
  AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle,
} from "@/components/ui/alert-dialog";

export default function AdminArticles() {
  const [articles, setArticles] = useState([]);
  const [toDelete, setToDelete] = useState(null);

  const load = () => api.get("/articles").then((r) => setArticles(r.data)).catch(() => {});
  useEffect(() => { load(); }, []); // eslint-disable-line react-hooks/exhaustive-deps

  const doDelete = async () => {
    try { await api.delete(`/articles/${toDelete.slug}`); toast.success("Deleted"); setToDelete(null); load(); }
    catch (e) { toast.error(formatApiErrorDetail(e.response?.data?.detail)); }
  };

  return (
    <div className="mx-auto max-w-5xl px-4 py-10 sm:px-6">
      <Link to="/admin" className="inline-flex items-center gap-1 text-sm hover:underline" style={{ color: "var(--arc-text-2)" }}><ArrowLeft size={14} /> Dashboard</Link>
      <div className="mt-4 flex flex-wrap items-center justify-between gap-3">
        <h1 className="font-serif-arc text-3xl font-black">Articles</h1>
        <ArcButton to="/admin/articles/new" variant="ghoulax" data-testid="new-article-btn"><Plus size={16} /> New Article</ArcButton>
      </div>

      <div className="mt-6 overflow-x-auto rounded-md border" style={{ backgroundColor: "var(--arc-card)" }}>
        <table className="w-full min-w-[600px] text-sm">
          <thead className="border-b text-left" style={{ color: "var(--arc-text-2)" }}>
            <tr>
              <th className="px-4 py-3 font-semibold">Title</th>
              <th className="px-4 py-3 font-semibold">Encyclopedia</th>
              <th className="px-4 py-3 font-semibold">Badge</th>
              <th className="px-4 py-3 font-semibold text-right">Actions</th>
            </tr>
          </thead>
          <tbody>
            {articles.map((a) => (
              <tr key={a.slug} className="border-b last:border-0" data-testid={`admin-article-row-${a.slug}`}>
                <td className="px-4 py-3">
                  <Link to={`/article/${a.slug}`} className="font-semibold hover:underline">{a.title}</Link>
                  <div className="text-xs" style={{ color: "var(--arc-text-2)" }}>/{a.slug}</div>
                </td>
                <td className="px-4 py-3">
                  <span className={a.encyclopedia === "ghoulax" ? "ghoulax-accent" : "zyfley-accent"}>{a.encyclopedia}</span>
                </td>
                <td className="px-4 py-3">{a.badge && <LabelBadge label={a.badge} />}</td>
                <td className="px-4 py-3">
                  <div className="flex justify-end gap-2">
                    <Link to={`/admin/articles/${a.slug}`} data-testid={`edit-${a.slug}`} className="flex h-8 w-8 items-center justify-center rounded border hover:bg-black/5 dark:hover:bg-white/10"><Pencil size={14} /></Link>
                    <button onClick={() => setToDelete(a)} data-testid={`delete-${a.slug}`} className="flex h-8 w-8 items-center justify-center rounded border text-red-600 hover:bg-red-50 dark:hover:bg-red-900/20"><Trash2 size={14} /></button>
                  </div>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      <AlertDialog open={!!toDelete} onOpenChange={(o) => !o && setToDelete(null)}>
        <AlertDialogContent style={{ backgroundColor: "var(--arc-card)" }}>
          <AlertDialogHeader>
            <AlertDialogTitle>Delete article?</AlertDialogTitle>
            <AlertDialogDescription>This permanently removes "{toDelete?.title}".</AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>Cancel</AlertDialogCancel>
            <AlertDialogAction onClick={doDelete} className="bg-red-600 hover:bg-red-700">Delete</AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
}
